import { hasOptedOut } from '@/lib/consent/opt-out'

// Marketing funnel events. Spec of record:
// ~/choice-graphics/marketing/conversion-events/README.md (wraps commit de00707)
// and site-tracking-contract.md sections 1 and 2.
//
// The GTM container (GTM-5Q979BT) is injected at the edge by Cloudflare's
// Google Tag Gateway on the production hostname; the app no longer bootstraps
// it (the old in-app loader used the custom dataLayer name 'script', so every
// push went to an array the injected container never read). Pushes target the
// standard window.dataLayer.
export const GTM_DATALAYER_NAME = 'dataLayer'

export type ValueBand = 'under_1k' | '1k_3k' | '3k_6k' | 'over_6k'

export type FunnelEvent =
  | { event: 'quote_started'; service_slug: string; category_slug: string }
  | {
      event: 'quote_completed'
      service_slugs: string[]
      vehicle_class: string
      confidence: string
      value_band: ValueBand
    }
  | { event: 'booking_confirmed'; service_slugs: string[]; value_band: ValueBand | 'unknown' }
  | { event: 'payment_completed'; value_band: ValueBand }

/** Lead forms that count as a lead for the Ads `Submit lead form` conversion (GTM tag 169). */
export type LeadFormName = 'quote' | 'contact' | 'fleet-quote' | 'colour-consult'

export function bandValue(totalCAD: number): ValueBand {
  if (totalCAD < 1000) return 'under_1k'
  if (totalCAD < 3000) return '1k_3k'
  if (totalCAD < 6000) return '3k_6k'
  return 'over_6k'
}

function dataLayer(): unknown[] | null {
  if (typeof window === 'undefined') return null
  const w = window as unknown as Record<string, unknown[]>
  w[GTM_DATALAYER_NAME] = w[GTM_DATALAYER_NAME] || []
  return w[GTM_DATALAYER_NAME]
}

export function pushFunnelEvent(evt: FunnelEvent, analyticsConsent: boolean): boolean {
  if (!analyticsConsent) return false
  const layer = dataLayer()
  if (!layer) return false
  layer.push(evt)
  return true
}

/**
 * A phone number in the shape enhanced conversions expects, or undefined when
 * the digits cannot be trusted to be a North American number. Ten digits take
 * the +1 country code; eleven starting with 1 already carry it. Anything else
 * (a partial number, an international one we cannot infer a country for) is
 * omitted rather than guessed, because a wrong number hashes to a wrong match.
 */
export function toE164(raw?: string | null): string | undefined {
  const digits = (raw ?? '').replace(/\D/g, '')
  if (digits.length === 10) return `+1${digits}`
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`
  return undefined
}

/**
 * The lead conversion the ads accounts bid on. Pushed once per successful
 * submission from the client component that owns the form, after the server
 * action has persisted the row. Implied consent allows it; a stored opt-out
 * stops it, because the custom HTML tags on this event would otherwise carry
 * the email and phone out regardless of consent mode (src/lib/consent/opt-out.ts).
 *
 * `user` carries the submitted email and phone for enhanced conversions
 * (site-tracking-contract.md section 2). The container's User-Provided Data
 * variable hashes both in the browser before anything leaves the page, and the
 * Ads account's match quality depends on them; the WordPress plugin this app
 * replaces has pushed the same two fields since 2025. Omit the argument on a
 * form that should not feed enhanced conversions, and the keys stay absent.
 */
export function pushFormSubmit(
  formName: LeadFormName,
  formId: string,
  user?: { email?: string | null; phone?: string | null }
): boolean {
  if (hasOptedOut()) return false
  const layer = dataLayer()
  if (!layer) return false
  const userEmail = user?.email?.trim().toLowerCase() || undefined
  const userPhone = toE164(user?.phone)
  layer.push({
    event: 'form_submit',
    form_name: formName,
    form_id: formId,
    cuft_tracked: true,
    cuft_source: 'app',
    submitted_at: new Date().toISOString(),
    ...(userEmail ? { user_email: userEmail } : {}),
    ...(userPhone ? { user_phone: userPhone } : {}),
  })
  return true
}

/**
 * `page_view` on client-side navigation
 * (site-tracking-contract.md section 5.3). App Router navigations never fire
 * the container's `gtm.js` event again, so the call-tracking number swap and
 * the click tracker would only ever run on the landing page; both tags carry a
 * `page_view` trigger for this push. Not consent-gated, and it carries no more
 * than the path.
 */
export function pushPageView(pagePath: string): boolean {
  const layer = dataLayer()
  if (!layer) return false
  layer.push({ event: 'page_view', page_path: pagePath })
  return true
}
